import React, { createContext } from 'react';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import _ from 'lodash';
import { ThemeConfig, ThemeOverrides } from './theme';
import DEFAULT_THEME from './default';
import { generateBrandColors } from './colorGen';

// Replaces the colors.next object with the generated brand colors
// if they aren't already provided. Fallback to the theme if there's an error.
const processTheme = (theme: ThemeOverrides | undefined): ThemeOverrides => {
  if (!theme) {
    return {};
  }

  if (!theme.colors?.next) {
    return theme;
  }

  try {
    const brandColors = generateBrandColors(theme.colors.next);

    return {
      ...theme,
      colors: {
        ...theme.colors,
        next: {
          ...theme.colors.next,
          ...brandColors,
        },
      },
    };
  } catch (error) {
    return theme;
  }
};

export const ThemeProviderContext = createContext<{
  useThemeNext: boolean;
  isDarkMode: boolean;
}>({
  useThemeNext: false,
  isDarkMode: false,
});

type ThemeProviderProps = React.PropsWithChildren<{
  theme?: ThemeOverrides | ThemeOverrides[];
  useThemeNext?: boolean;
  isDarkMode?: boolean;
}>;

const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  theme,
  useThemeNext = false,
  isDarkMode = false,
}) => {
  const defaultTheme = _.cloneDeep(DEFAULT_THEME);
  const resolvedTheme: ThemeConfig = Array.isArray(theme)
    ? _.merge(defaultTheme, ...theme.map(processTheme))
    : _.merge(defaultTheme, processTheme(theme));

  return (
    <ThemeProviderContext.Provider
      value={{ useThemeNext, isDarkMode: useThemeNext && isDarkMode }}
    >
      <EmotionThemeProvider theme={resolvedTheme}>
        {children}
      </EmotionThemeProvider>
    </ThemeProviderContext.Provider>
  );
};

export default ThemeProvider;
